// Shared data for the "Regal Eyes - Social 2026" demo campaign.
//
// Source: Regal Eyes Location Spends Report - Feb/Mar/Apr 2026.
//
// This module is the single source of truth for:
//   - the advertiser id that is allowed to see the Map View Report
//   - the synthetic campaign id / name surfaced in the Campaigns list
//   - the per-Riyadh-district delivery data set
// Everything else (sidebar gating, campaigns list injection, campaign
// details page, Map View Report) imports from here.

export const REGAL_EYES_ADVERTISER_ID = 284

export const REGAL_EYES_CAMPAIGN_ID = 'regal-eyes-social-2026'
export const REGAL_EYES_CAMPAIGN_NAME = 'Regal Eyes - Social 2026'

// Approximate centroid lat/lng for each Riyadh district. Names + metrics
// are taken verbatim from the Excel sheet's shared strings table.
export const RIYADH_AREA_DATA = [
  { area: 'KAFD',                    lat: 24.7641, lng: 46.6398, spend: 2718.1053,   impressions: 1576759, clicks: 4316 },
  { area: 'Hittin',                  lat: 24.7497, lng: 46.6066, spend: 2543.4753,   impressions: 1572622, clicks: 4519 },
  { area: 'Al Olaya',                lat: 24.6936, lng: 46.6826, spend: 2417.4053,   impressions: 1575426, clicks: 4744 },
  { area: 'Al Malqa',                lat: 24.7864, lng: 46.6253, spend: 2220.53,     impressions: 1625715, clicks: 6139 },
  { area: 'Al Yasmin',               lat: 24.8217, lng: 46.6356, spend: 2084.43,     impressions: 1592485, clicks: 6353 },
  { area: 'Al Narjis',               lat: 24.8488, lng: 46.6716, spend: 1951.471053, impressions: 1621928, clicks: 6799 },
  { area: 'Al Aqiq',                 lat: 24.7619, lng: 46.6494, spend: 1830.3,      impressions: 1562884, clicks: 6328 },
  { area: 'Al Sulimaniyah',          lat: 24.6997, lng: 46.7152, spend: 2074.43,     impressions: 1890637, clicks: 8355 },
  { area: 'Al Wurud',                lat: 24.7167, lng: 46.6917, spend: 1690.49,     impressions: 1591462, clicks: 6227 },
  { area: 'Al Rahmaniyah',           lat: 24.7831, lng: 46.6708, spend: 1631.71,     impressions: 1602079, clicks: 6160 },
  { area: 'Airport / Business Gate', lat: 24.9576, lng: 46.6986, spend: 1559,        impressions: 1600207, clicks: 5707 },
  { area: 'Al Rawdah',               lat: 24.7414, lng: 46.7833, spend: 1520.54,     impressions: 1870565, clicks: 6043 },
  { area: 'Ishbiliyah',              lat: 24.7833, lng: 46.8167, spend: 1512.1,      impressions: 1880023, clicks: 6337 },
  { area: 'Al Hamra',                lat: 24.7833, lng: 46.7833, spend: 1315.16,     impressions: 1765093, clicks: 5450 },
  { area: 'Al Malaz',                lat: 24.6731, lng: 46.7331, spend: 1270.4,      impressions: 1808794, clicks: 5335 },
  { area: 'Airport West',            lat: 24.9576, lng: 46.6500, spend: 1232.78,     impressions: 1798336, clicks: 5052 },
  { area: 'Al Nasiriyah',            lat: 24.6494, lng: 46.6961, spend:  951.73,     impressions: 1562884, clicks: 3841 },
  { area: 'Logistics Zone',          lat: 24.9000, lng: 46.7500, spend:  830.58,     impressions: 1606242, clicks: 3615 },
  { area: 'Al Murabba',              lat: 24.6536, lng: 46.7042, spend:  806.95,     impressions: 1615405, clicks: 3746 },
  { area: 'Al Shifa',                lat: 24.5781, lng: 46.7367, spend:  156.18,     impressions:  426191, clicks:  876 },
  { area: 'Al Aziziyah',             lat: 24.5683, lng: 46.7311, spend:  143.2,      impressions:  444375, clicks:  849 },
]

function sum(arr, key) {
  return arr.reduce((acc, row) => acc + row[key], 0)
}

export const REGAL_EYES_TOTALS = (() => {
  const spend = sum(RIYADH_AREA_DATA, 'spend')
  const impressions = sum(RIYADH_AREA_DATA, 'impressions')
  const clicks = sum(RIYADH_AREA_DATA, 'clicks')
  const ctr = impressions > 0 ? clicks / impressions : 0
  return { spend, impressions, clicks, ctr }
})()

// Source: Regal Eyes Report Latest 18May.xlsx
// (Total tab + per-platform monthly tabs).
//
// Spend per platform per month for the same Feb–Apr 2026 campaign window.
// Order of platforms matches the order in the report's "Total" sheet so
// the donut + stacked chart line up.
export const REGAL_EYES_MONTHS = ['Feb', 'Mar', 'Apr']

export const REGAL_EYES_PLATFORM_MONTHLY = [
  { platform: 'Meta',                      Feb: 6034.5142857142862, Mar: 2828.306,             Apr: 3526.8609999999999 },
  { platform: 'Snapchat',                  Feb: 4249.7285714285713, Mar: 1713.413,             Apr: 4390.932             },
  { platform: 'TikTok',                    Feb: 1917.2,             Mar: 1143.493,             Apr:  843.947             },
  { platform: 'Google PerfMax & Shopping', Feb:    3.406,           Mar: 1868.035,             Apr: 1504.516             },
  { platform: 'Google Search',             Feb:   45.2714285714286, Mar:  790.504,             Apr:  937.417             },
  { platform: 'FoxPush',                   Feb:   91.4285714285714, Mar:  209.508,             Apr:  362.83              },
]

export const REGAL_EYES_PLATFORMS = (() => {
  const grandTotal = REGAL_EYES_PLATFORM_MONTHLY.reduce(
    (acc, row) => acc + row.Feb + row.Mar + row.Apr,
    0
  )
  return REGAL_EYES_PLATFORM_MONTHLY.map((row) => {
    const total = row.Feb + row.Mar + row.Apr
    return {
      platform: row.platform,
      spend: total,
      share: grandTotal > 0 ? total / grandTotal : 0,
    }
  })
})()

export const REGAL_EYES_MONTHLY_TOTALS = REGAL_EYES_MONTHS.map((month) => ({
  month,
  spend: REGAL_EYES_PLATFORM_MONTHLY.reduce((acc, row) => acc + row[month], 0),
}))

// Realistic budget / pacing numbers for the demo campaign.
export const REGAL_EYES_BUDGET = 50000 // total campaign budget
export const REGAL_EYES_DAYS_REMAINING = 0

// Audience demographics for the Regal Eyes - Social 2026 campaign.
// Percentages, not raw counts. They are roughly representative of a
// Saudi-market social-buy targeting urban Riyadh districts.
export const REGAL_EYES_DEMOGRAPHICS = {
  gender: [
    { label: 'Female', value: 58 },
    { label: 'Male',   value: 40 },
    { label: 'N/A',    value:  2 },
  ],
  age: [
    { label: '13-17', value:  4 },
    { label: '18-25', value: 28 },
    { label: '26-35', value: 32 },
    { label: '36-50', value: 22 },
    { label: '51-65', value:  9 },
    { label: '65+',   value:  3 },
    { label: 'N/A',   value:  2 },
  ],
  device: [
    { label: 'Mobile',  value: 78 },
    { label: 'Desktop', value: 14 },
    { label: 'Tablet',  value:  8 },
  ],
  os: [
    { label: 'iOS',     value: 52 },
    { label: 'Android', value: 45 },
    { label: 'Other',   value:  3 },
  ],
  placement: [
    { label: 'Feed',    value: 60 },
    { label: 'Stories', value: 28 },
    { label: 'Reels',   value: 10 },
    { label: 'Other',   value:  2 },
  ],
}

// Returns the advertiser id of the currently logged-in user, or 0 if no
// advertiser is set. Always tolerates a missing / malformed localStorage.
export function getCurrentAdvertiserId() {
  try {
    const raw = localStorage.getItem('auth.currentUser')
    if (!raw) return 0
    const user = JSON.parse(raw)
    if (user && user.advertiser && user.advertiser.id != null) {
      return Number(user.advertiser.id) || 0
    }
    return 0
  } catch (_) {
    return 0
  }
}

export function isRegalEyesAdvertiser() {
  return getCurrentAdvertiserId() === REGAL_EYES_ADVERTISER_ID
}
